import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import DisplayRichContent from '../../../global/components/forms/DisplayRichContent';
import { checkIfRichText } from '../../../global/utils/richContentUtils';


const LandingTemplateFooter = ({
  footer
  , footerLogoUrl
  , footerCaption
  , expandedFooterClass = ''
  , persistentFooter
  , persistentFooterLogoUrl
  , showPfLogo
  , customFontMap
}) => {

  const [isAutoExpand, setIsAutoExpand] = useState(false);

  const hasFooterContent = checkIfRichText(footer) || checkIfRichText(persistentFooter) || footerCaption;

  const handleScroll = useCallback((e) => {
    if(!checkIfRichText(persistentFooter)) return;
    // get main content element so we can calculate if we're at the bottom of the page
    const mainContent = document.getElementById('main-content');
    if(!mainContent) return;
    const collapsedFooterHeight = 168;
    // visualViewport is used to get the height of the viewport on mobile devices, try that first, if it's not available use window.innerHeight which works well on desktop
    const windowHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    const windowScrollHeight = Math.ceil(window.scrollY + windowHeight); // round up to account for fractional pixels
    const mainContentHeight = mainContent.offsetTop + mainContent.offsetHeight + collapsedFooterHeight;
    const isAtBottomOfContent = windowScrollHeight >= mainContentHeight;
    if(isAtBottomOfContent) {
      if(!isAutoExpand) setIsAutoExpand(true);
    } else if(isAutoExpand) {
      setIsAutoExpand(false);
    }
  }, [isAutoExpand]);

  // sticky footer scroll
  useEffect(() => {
    if(!checkIfRichText(persistentFooter)) return;
    window.addEventListener('scroll', handleScroll);
    // need resize event too for mobile browsers that change the height when they hide the address bar or rotate the device
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    };
  }, [handleScroll, persistentFooter]);

  return (
    <footer
      className={`max-w-screen w-[calc(100%+2rem)] sm:w-[calc(100%+10em)] -mx-4 sm:-mx-20 px-4 sm:px-20 bg-inherit ${hasFooterContent ? "py-4 border-t-2" : " hidden"} ${isAutoExpand ? `sticky overflow-y-auto top-10 ${expandedFooterClass}`  : checkIfRichText(persistentFooter) ? 'max-h-[168px] overflow-hidden sticky bottom-0' : 'max-h-[168px] bottom-0'}`}
      id="footer-top"
    >
      {checkIfRichText(persistentFooter) ?
        <div>
          <div className="flex">
            {persistentFooterLogoUrl || showPfLogo ?
              <div className="w-full flex flex-row py-2 gap-4 items-center justify-between">
                {persistentFooterLogoUrl && (
                  <img src={persistentFooterLogoUrl} alt="Client Logo" className="h-auto max-h-12 md:h-12 w-auto max-w-[50%]" />
                )}
              </div>
              : null
            }
            <div className='flex w-full justify-end sticky top-0'>
              <button
                className="btn-text text-pf-black-700 btn-sm bg-transparent"
                onClick={() => {
                  if(isAutoExpand) {
                    const headerTop = document.getElementById('header-top');
                    headerTop?.scrollIntoView({ behavior: 'instant' });
                  } else {
                    const footerContent = document.getElementById('footer-top');
                    setIsAutoExpand(true);
                    setTimeout(() => {
                      footerContent?.scrollIntoView({ behavior: 'instant' });
                    }, 100);
                  }
                }}
              >
                {isAutoExpand ?
                  <div className="flex items-center gap-2">
                    <span>Collapse</span>
                    <i className="fa fa-chevron-down" />
                  </div>
                  :
                  <div className="flex items-center gap-2">
                    <span>Expand</span>
                    <i className="fa fa-chevron-up" />
                  </div>
                }
              </button>
            </div>
          </div>
          <div className="mb-2">
            <DisplayRichContent content={persistentFooter} customFontMap={customFontMap} />
          </div>
        </div>
        : null
      }
      <div className="pb-6">
        {checkIfRichText(footer) && (
          <div className="mb-2">
            <DisplayRichContent content={footer} customFontMap={customFontMap} />
          </div>
        )}
        {showPfLogo || footerLogoUrl || footerCaption ?
          <div className="flex flex-col md:flex-row mt-8 py-2 gap-4 md:items-center justify-between">
            <div className="w-full md:flex gap-2">
              {footerLogoUrl && (
                <img src={footerLogoUrl} alt="Client Logo" className="h-auto max-h-12 md:h-12 w-auto max-w-[50%] mb-4 md:mb-0" />
              )}
              {footerCaption && (
                <div className="flex flex-col my-auto justify-end whitespace-pre-line">
                  <p className="text-xs">{footerCaption}</p>
                </div>
              )}
            </div>
            {showPfLogo && (
              <div className="flex w-fit items-center md:ml-auto">
                <p className="my-auto text-xs md:text-sm text-pf-neutrone-500 italic mr-3">Powered by</p>
                <img className="h-auto max-h-12 w-auto my-auto flex justify-end max-w-[50%]" src="https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png" />
              </div>
            )}
          </div>
          : null
        }
      </div>
    </footer>
  )
}

LandingTemplateFooter.propTypes = {
  footer: PropTypes.object
  , footerLogoUrl: PropTypes.string
  , footerCaption: PropTypes.string
  , expandedFooterClass: PropTypes.string
  , persistentFooter: PropTypes.object
  , persistentFooterLogoUrl: PropTypes.string
  , showPfLogo: PropTypes.bool
  , customFontMap: PropTypes.object
}

export default LandingTemplateFooter;